import React, { useState, useEffect } from 'react';
import { QrCode, Radio } from 'lucide-react';

// Custom hook for responsive breakpoints (same as original)
const useResponsive = () => {
  const [screenWidth, setScreenWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200);

  useEffect(() => {
    const handleResize = () => setScreenWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return {
    screenWidth,
    isMobile: screenWidth < 640,
    isTablet: screenWidth >= 640 && screenWidth < 1024,
    isDesktop: screenWidth >= 1024,
    isSmall: screenWidth < 375,
    isLarge: screenWidth >= 1440
  };
};

const HotmessHero = ({
  ctaButtonUrl = "#",
  qrCodeUrl = "#",
  radioUrl = "#",
  joinQrUrl = "#"
}) => {
  const { isMobile, isTablet, isDesktop, isSmall } = useResponsive();
  const [isScanHovered, setIsScanHovered] = useState(false);
  const [isRadioHovered, setIsRadioHovered] = useState(false);

  const handleButtonClick = (url) => {
    if (url && url !== "#") {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  const getHeroStyles = () => ({
    container: {
      width: '100%',
      backgroundColor: '#000000',
      padding: isMobile ? '40px 16px 100px 16px' : isTablet ? '60px 24px 120px 24px' : '80px 32px',
      minHeight: isMobile ? '100vh' : '700px',
      boxSizing: 'border-box',
      position: 'relative'
    },
    wrapper: {
      maxWidth: '1280px',
      margin: '0 auto',
      width: '100%',
      display: 'flex',
      flexDirection: isMobile ? 'column' : 'row',
      alignItems: isMobile ? 'flex-start' : 'center',
      gap: isMobile ? '40px' : isTablet ? '60px' : '80px',
      minHeight: isMobile ? 'calc(100vh - 220px)' : 'auto',
      paddingBottom: isMobile ? '80px' : '0'
    },
    productSection: {
      flex: isMobile ? 'none' : '0 0 auto',
      display: 'flex',
      justifyContent: isMobile ? 'center' : 'flex-start',
      alignItems: 'center',
      order: isMobile ? 2 : 0,
      width: isMobile ? '100%' : 'auto'
    },
    productContainer: {
      position: 'relative',
      width: isMobile ? '240px' : isTablet ? '280px' : '320px',
      height: isMobile ? '340px' : isTablet ? '400px' : '460px',
      backgroundColor: '#FFFFFF',
      borderRadius: isMobile ? '16px' : '20px',
      padding: isMobile ? '16px' : '20px',
      boxSizing: 'border-box',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
    },
    productImage: {
      width: '100%',
      height: '100%',
      objectFit: 'contain',
      borderRadius: isMobile ? '12px' : '16px'
    },
    contentSection: {
      flex: '1',
      display: 'flex',
      flexDirection: 'column',
      alignItems: isMobile ? 'flex-start' : 'flex-start',
      textAlign: isMobile ? 'left' : 'left',
      order: isMobile ? 1 : 1,
      width: '100%'
    },
    mainTitle: {
      fontSize: isSmall ? '28px' : isMobile ? '32px' : isTablet ? '48px' : '56px',
      fontWeight: '900',
      color: '#FFFFFF',
      textTransform: 'uppercase',
      letterSpacing: isMobile ? '1px' : '2px',
      lineHeight: '1.1',
      marginBottom: isMobile ? '12px' : '16px'
    },
    subtitle: {
      fontSize: isSmall ? '14px' : isMobile ? '16px' : isTablet ? '18px' : '20px',
      fontWeight: '500',
      color: '#CCCCCC',
      lineHeight: '1.5',
      marginBottom: isMobile ? '24px' : '32px',
      maxWidth: '100%'
    },
    ctaButton: {
      background: 'linear-gradient(135deg, #EC4899, #BE185D)',
      color: '#FFFFFF',
      fontSize: isSmall ? '14px' : isMobile ? '16px' : '18px',
      fontWeight: '700',
      textTransform: 'uppercase',
      letterSpacing: '1px',
      padding: isMobile ? '14px 28px' : '16px 32px',
      borderRadius: '50px',
      border: 'none',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      marginBottom: isMobile ? '24px' : '32px',
      boxShadow: '0 0 30px rgba(236, 72, 153, 0.6), 0 8px 24px rgba(236, 72, 153, 0.3)',
      alignSelf: 'flex-start'
    },
    ctaButtonHover: {
      background: 'linear-gradient(135deg, #F472B6, #EC4899)',
      transform: 'translateY(-2px)',
      boxShadow: '0 0 40px rgba(236, 72, 153, 0.8), 0 12px 32px rgba(236, 72, 153, 0.4)'
    },
    bottomSection: {
      display: 'flex',
      flexDirection: isMobile ? 'column' : 'row',
      alignItems: 'flex-start',
      gap: isMobile ? '12px' : '24px'
    },
    bottomItem: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      padding: '8px 16px',
      borderRadius: '8px'
    },
    bottomItemHover: {
      backgroundColor: 'rgba(255, 255, 255, 0.05)',
      transform: 'translateY(-1px)'
    },
    bottomIcon: {
      width: '20px',
      height: '20px',
      color: '#EC4899'
    },
    bottomText: {
      fontSize: isSmall ? '12px' : isMobile ? '14px' : '16px',
      fontWeight: '600',
      color: '#CCCCCC',
      letterSpacing: '0.3px'
    },
    hotmessRadioText: {
      fontSize: isSmall ? '12px' : isMobile ? '14px' : '16px',
      fontWeight: '600',
      color: '#EC4899',
      letterSpacing: '0.3px',
      textDecoration: 'underline'
    },
    joinSection: {
      position: 'absolute',
      bottom: isMobile ? '16px' : '32px',
      left: isMobile ? '16px' : '32px',
      right: isMobile ? '16px' : 'auto',
      zIndex: 10,
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      padding: '8px',
      borderRadius: '8px'
    },
    joinSectionHover: {
      backgroundColor: 'rgba(255, 255, 255, 0.05)'
    },
    joinTitle: {
      fontSize: isSmall ? '12px' : isMobile ? '14px' : '16px',
      fontWeight: '700',
      color: '#FFFFFF',
      textTransform: 'uppercase',
      letterSpacing: '1px',
      marginBottom: '8px'
    },
    joinDescription: {
      fontSize: isSmall ? '10px' : isMobile ? '12px' : '14px',
      fontWeight: '400',
      color: '#666666',
      lineHeight: '1.4',
      maxWidth: '300px'
    }
  });

  const styles = getHeroStyles();

  return (
    <div style={styles.container}>
      <div style={styles.wrapper}>
        <div style={styles.productSection}>
          <div style={styles.productContainer}>
            <img 
              src="/images/hnh.avif" 
              alt="HOTMESS Product" 
              style={styles.productImage} 
            />
          </div>
        </div>

        <div style={styles.contentSection}>
          <h1 style={styles.mainTitle}>
            HARD. HEALTHY. HAPPY.
          </h1>
          <p style={styles.subtitle}>
            The only lube with a care system, a QR code, and a 24/7 radio station.
          </p>
          
          <button 
            style={{
              ...styles.ctaButton,
              ...(isScanHovered ? styles.ctaButtonHover : {})
            }}
            onMouseEnter={() => setIsScanHovered(true)}
            onMouseLeave={() => setIsScanHovered(false)}
            onClick={() => handleButtonClick(ctaButtonUrl)}
          >
            SCAN IN. GET WET.
          </button>

          <div style={styles.bottomSection}>
            <div
              style={{
                ...styles.bottomItem,
                ...(isScanHovered ? styles.bottomItemHover : {})
              }}
              onMouseEnter={() => setIsScanHovered(true)}
              onMouseLeave={() => setIsScanHovered(false)}
              onClick={() => handleButtonClick(qrCodeUrl)}
            >
              <QrCode style={styles.bottomIcon} />
              <span style={styles.bottomText}>
                Scan the QR for care & support
              </span>
            </div>

            <div
              style={{
                ...styles.bottomItem,
                ...(isRadioHovered ? styles.bottomItemHover : {})
              }}
              onMouseEnter={() => setIsRadioHovered(true)}
              onMouseLeave={() => setIsRadioHovered(false)}
              onClick={() => handleButtonClick(radioUrl)}
            >
              <Radio style={styles.bottomIcon} />
              <span style={styles.hotmessRadioText}>
                HOTMESS Radio
              </span>
            </div>
          </div>
        </div>
      </div>

      <div 
        style={styles.joinSection}
        onClick={() => handleButtonClick(joinQrUrl)}
      >
        <h3 style={styles.joinTitle}>
          JOIN THE MESS
        </h3>
        <p style={styles.joinDescription}>
          QR code placeholder. Scan to join our community and unlock live events.
        </p>
      </div>
    </div>
  );
};

// Example usage with URLs
const App = () => {
  return (
    <HotmessHero
      ctaButtonUrl="https://hnhmess.com/"
      qrCodeUrl="/"
      radioUrl="https://hotmessldn.com/"
      joinQrUrl="/"
    />
  );
};

export default App;